import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronLeft, Crown, BookOpen, Coins, Users, Heart, 
  Calendar, Camera, CheckCircle, Star, Target, Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const ROLES = [
  {
    id: 'owner',
    title: 'Family Owner',
    icon: Crown,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10',
    borderColor: 'border-yellow-500/30',
    description: 'The founder and primary administrator of the family group.',
    isSystemRole: true,
    permissions: [
      'Edit family name and description',
      'Archive or restore the family',
      'Transfer ownership to another member',
      'All permissions of other roles',
      'Remove members from the family',
      'Delete the family permanently'
    ],
    responsibilities: [
      'Set the overall vision and purpose for the family group',
      'Ensure the family platform is being used effectively',
      'Delegate responsibilities by assigning roles to members',
      'Resolve disputes that other members cannot handle',
      'Make final decisions on family governance matters'
    ],
    gettingStarted: [
      'Complete your family profile with a meaningful description',
      'Invite key family members to join',
      'Assign roles to distribute leadership responsibilities',
      'Set up the dues structure if your family collects contributions',
      'Create your first family event to bring everyone together'
    ]
  },
  {
    id: 'vision_keeper',
    title: 'Vision Keeper',
    icon: Target,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    description: 'Sets the long-term direction and goals for the family.',
    permissions: [
      'Edit family description and mission',
      'Create and manage family goals',
      'Pin important announcements',
      'Set family values and principles'
    ],
    responsibilities: [
      'Articulate the family\'s shared vision and values',
      'Facilitate discussions about family direction',
      'Document and communicate family goals',
      'Inspire members to work toward common objectives',
      'Review and update the family mission annually'
    ],
    gettingStarted: [
      'Draft a family mission statement',
      'Gather input from elders about family values',
      'Create 3-5 family goals for the year',
      'Share the vision in a family message',
      'Schedule quarterly vision review sessions'
    ]
  },
  {
    id: 'scribe',
    title: 'Scribe',
    icon: BookOpen,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    description: 'Documents family history, stories, and important decisions.',
    permissions: [
      'Create and edit family stories',
      'Manage timeline entries',
      'Edit stories submitted by others',
      'Archive or feature important stories'
    ],
    responsibilities: [
      'Collect and document family stories from all generations',
      'Interview elders to preserve oral histories',
      'Maintain the family timeline with milestones',
      'Record important family decisions and their context',
      'Ensure stories are accurate and respectful'
    ],
    gettingStarted: [
      'Review existing stories in the Spiritual section',
      'Reach out to elders for interview sessions',
      'Create a story collection schedule',
      'Add key historical milestones to the timeline',
      'Encourage members to share their own stories'
    ]
  },
  {
    id: 'harvest_guardian',
    title: 'Harvest Guardian',
    icon: Coins,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    description: 'Manages family finances and resource allocation.',
    permissions: [
      'Configure dues and billing cycles',
      'View all member payment history',
      'Record payments on behalf of members',
      'Charge dues to all members',
      'Allocate funds to events',
      'Generate financial reports'
    ],
    responsibilities: [
      'Set up and maintain the family dues structure',
      'Track payments and follow up on outstanding balances',
      'Provide transparent financial updates to members',
      'Allocate collected funds to family events and needs',
      'Maintain accurate financial records'
    ],
    gettingStarted: [
      'Review the current dues configuration',
      'Ensure all members have dues accounts',
      'Set up a regular billing schedule',
      'Create a system for payment reminders',
      'Plan how funds will be allocated to events'
    ]
  },
  {
    id: 'bridge_builder',
    title: 'Bridge Builder',
    icon: Users,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    description: 'Maintains connections and resolves conflicts between family members.',
    permissions: [
      'Send priority messages to all members',
      'Moderate chat discussions',
      'Create reconciliation events',
      'Access conflict resolution tools'
    ],
    responsibilities: [
      'Foster positive communication between members',
      'Mediate disagreements before they escalate',
      'Reach out to inactive or disconnected members',
      'Organize activities that bring different branches together',
      'Be a trusted confidant for family concerns'
    ],
    gettingStarted: [
      'Introduce yourself as the Bridge Builder',
      'Check in with members who seem disconnected',
      'Create a welcome message for new members',
      'Establish guidelines for respectful communication',
      'Plan a casual gathering for relationship building'
    ]
  },
  {
    id: 'legacy_anchor',
    title: 'Legacy Anchor',
    icon: Heart,
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
    description: 'Preserves traditions and passes down cultural heritage.',
    permissions: [
      'Manage spiritual content and prayers',
      'Create and edit family traditions',
      'Curate gratitude and devotional content',
      'Organize heritage events'
    ],
    responsibilities: [
      'Document and preserve family traditions',
      'Ensure cultural practices are passed to younger generations',
      'Coordinate spiritual and religious observances',
      'Collect family recipes, customs, and rituals',
      'Educate new family members about heritage'
    ],
    gettingStarted: [
      'List all family traditions you know of',
      'Interview elders about traditions from their childhood',
      'Document recipes with stories behind them',
      'Create a calendar of traditional observances',
      'Share a tradition story each month'
    ]
  },
  {
    id: 'circle_steward',
    title: 'Circle Steward',
    icon: Calendar,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/30',
    description: 'Organizes gatherings and ensures inclusive participation.',
    permissions: [
      'Create and manage family events',
      'Send event invitations and reminders',
      'Manage RSVPs and attendance',
      'Assign event tasks to members',
      'Set event budgets'
    ],
    responsibilities: [
      'Plan and coordinate family gatherings',
      'Ensure events are accessible to all members',
      'Manage logistics (venue, food, activities)',
      'Follow up on RSVPs and encourage attendance',
      'Delegate tasks and track completion'
    ],
    gettingStarted: [
      'Review past events to understand preferences',
      'Survey members about scheduling constraints',
      'Plan the next family gathering',
      'Create an event checklist template',
      'Build a volunteer list for event help'
    ]
  },
  {
    id: 'archivist',
    title: 'Archivist',
    icon: Camera,
    color: 'text-pink-500',
    bgColor: 'bg-pink-500/10',
    borderColor: 'border-pink-500/30',
    description: 'Maintains photos, documents, and family memorabilia.',
    permissions: [
      'Manage photo albums and galleries',
      'Upload and organize photos',
      'Create shareable album links',
      'Edit photo captions and metadata',
      'Archive or feature albums'
    ],
    responsibilities: [
      'Collect photos from family events and members',
      'Organize photos into meaningful albums',
      'Ensure photos are properly labeled with dates and names',
      'Digitize old physical photos when possible',
      'Share albums with the broader family'
    ],
    gettingStarted: [
      'Review existing photos in the gallery',
      'Create albums for major events',
      'Reach out to members for photo contributions',
      'Set up a system for collecting event photos',
      'Label unidentified people in old photos'
    ]
  }
];

export default function RoleGuide() {
  const [selectedRole, setSelectedRole] = useState('owner');
  const role = ROLES.find(r => r.id === selectedRole);
  const Icon = role?.icon || Crown;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard" className="text-gray-400 hover:text-white">
            <ChevronLeft className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Shield className="w-8 h-8 text-blue-400" />
              Family Role Guide
            </h1>
            <p className="text-gray-400">Understanding leadership responsibilities in your family</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Role Selector */}
          <div className="lg:col-span-1">
            <Card className="bg-slate-800 border-slate-700 sticky top-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-lg">Select a Role</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {ROLES.map((r) => {
                  const RoleIcon = r.icon;
                  return (
                    <button
                      key={r.id}
                      onClick={() => setSelectedRole(r.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                        selectedRole === r.id 
                          ? `${r.bgColor} ${r.borderColor} border-2` 
                          : 'hover:bg-slate-700 border-2 border-transparent'
                      }`}
                    >
                      <RoleIcon className={`w-5 h-5 ${r.color}`} />
                      <span className={selectedRole === r.id ? 'text-white font-medium' : 'text-gray-300'}>
                        {r.title}
                      </span>
                      {r.isSystemRole && (
                        <Badge className="ml-auto bg-yellow-600 text-xs">System</Badge>
                      )}
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Role Details */}
          <div className="lg:col-span-3 space-y-6">
            {/* Role Header */}
            <Card className={`${role.bgColor} ${role.borderColor} border-2`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`p-4 rounded-xl bg-slate-800`}>
                    <Icon className={`w-10 h-10 ${role.color}`} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                      {role.title}
                      {role.isSystemRole && (
                        <Badge className="bg-yellow-600">System Role</Badge>
                      )}
                    </h2>
                    <p className="text-gray-300 mt-1">{role.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tabs for Details */}
            <Tabs defaultValue="permissions" className="w-full">
              <TabsList className="bg-slate-700 border border-slate-500 p-1">
                <TabsTrigger 
                  value="permissions" 
                  className="text-white font-medium hover:bg-slate-500 transition-colors data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:font-bold"
                >
                  Permissions
                </TabsTrigger>
                <TabsTrigger 
                  value="responsibilities" 
                  className="text-white font-medium hover:bg-slate-500 transition-colors data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:font-bold"
                >
                  Responsibilities
                </TabsTrigger>
                <TabsTrigger 
                  value="getting-started" 
                  className="text-white font-medium hover:bg-slate-500 transition-colors data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:font-bold"
                >
                  Getting Started
                </TabsTrigger>
              </TabsList>

              <TabsContent value="permissions">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-400" />
                      What This Role Can Do
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-3">
                      {role.permissions.map((permission) => (
                        <div key={permission} className="flex items-start gap-3 p-3 bg-slate-700/50 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-200">{permission}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="responsibilities">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Star className="w-5 h-5 text-yellow-400" />
                      Key Responsibilities
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {role.responsibilities.map((resp, i) => (
                        <div key={`resp-${i}`} className="flex items-start gap-3 p-4 bg-slate-700/50 rounded-lg">
                          <span className={`w-8 h-8 rounded-full ${role.bgColor} flex items-center justify-center flex-shrink-0 font-bold ${role.color}`}>
                            {i + 1}
                          </span>
                          <span className="text-gray-200 pt-1">{resp}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="getting-started">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Target className="w-5 h-5 text-purple-400" />
                      First Steps for New {role.title}s
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {role.gettingStarted.map((step, i) => (
                        <div key={`step-${i}`} className="flex items-start gap-3 p-4 bg-slate-700/50 rounded-lg border-l-4 border-purple-500">
                          <span className="w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0 text-white text-sm font-bold">
                            {i + 1}
                          </span>
                          <span className="text-gray-200">{step}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <p className="text-blue-300 text-sm">
                        <strong>Tip:</strong> Don't try to do everything at once. Focus on one step at a time and build momentum gradually.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
